// ============================================================
// หน้า WriteOfDept — การตัดลูกหนี้ (ตัดหนี้ของบ้านเลขที่หนึ่ง)
// ------------------------------------------------------------
// เข้ามาจากหน้า "ข้อมูลค่าใช้จ่ายและการจัดเก็บฯ" พร้อม ?address_number=xxx
// แสดงบิลค้างทุกงวดของบ้านหลังนั้น ให้เลือกว่าจะตัดหนี้ยอดไหน
//
// มี 2 โหมด ใช้คนละ API
//   "old" ตัดทั้งยอด        — ติ๊กเลือกทั้งงวด ตัดทั้งใบ
//   "new" เลือกตัดเฉพาะรายการ — ติ๊กเลือกเป็นช่อง ๆ (ตัดเฉพาะค่าน้ำ / เฉพาะค่าปรับ ฯลฯ)
//
// ต้องระบุสาเหตุการตัดหนี้ และอ้างอิงเลขที่ใบเสร็จได้ (ถ้ามี)
// ============================================================

import React, { Component } from "react";

import "../../shared/config/global.js";
import "../../assets/css/bootstrap.css";
import Navbar from "../../shared/layout/Navbar.js";
import { tokens, total, success_url, toFixed, GET, PUT, format_date, DatePicker, getStorage, loading, alert_2, isWaived } from "../../shared/index.js";

// รายการที่เลือกตัดได้ในโหมด "เลือกตัดเฉพาะรายการ"
//   compute = คำนวณจากหลายฟิลด์ (ถ้าไม่ใช่ค่าตรง ๆ ของ item)
const CUT_FIELDS = [
  { key: "water_bill", label: "ค่าน้ำประปา" },
  { key: "equipment_maintenance_fee", label: "ค่ารักษาอุปกรณ์น้ำ" },
  { key: "common_expenses", label: "ค่าส่วนกลาง" },
  { key: "waste_cost", label: "ค่าขยะ" },
  { key: "cable_cost", label: "ค่าเคเบิล" },
  { key: "other_income", label: "ค่าใช้จ่ายอื่นๆ" },
  { key: "fire_cost", label: "ค่าเบี้ยประกัน" },
  { key: "debt_adjust", label: "เพิ่ม/ลดหนี้", compute: (item) => (item.add_debt || 0) - (item.reduce_debt || 0) },
  { key: "meter_connect_cost", label: "ค่าต่อกลับมิเตอร์" },
  { key: "late_payment", label: "ค่าปรับชำระล่าช้า" },
  { key: "adjustment", label: "ปรับปรุงยอดเงิน", compute: () => 0 },
];

// ช่องค่าใช้จ่ายในบิล คู่กับฟิลด์สถานะ (s_*) และตัวคูณตอนรวมยอด
const BILL_FIELDS = [
  { k: "water_bill", sk: "s_water_bill" },
  { k: "waste_cost", sk: "s_waste_cost" },
  { k: "equipment_maintenance_fee", sk: "s_equipment_maintenance_fee" },
  { k: "common_expenses", sk: "s_common_expenses" },
  { k: "cable_cost", sk: "s_cable_cost" },
  { k: "fire_cost", sk: "s_fire_cost" },
  { k: "other_income", sk: "s_other_income" },
  { k: "add_debt", sk: "s_add_debt" },
  { k: "meter_connect_cost", sk: "s_meter_connect_cost" },
  { k: "reduce_debt", sk: "s_reduce_debt", m: -1 },
  { k: "late_payment", sk: "s_late_payment" },
];

// ค่าที่ backend อาจส่งมาเป็นข้อความมี comma ต้องแปลงเป็นตัวเลขก่อนคิด
const COMMA_FIELDS = ["late_payment", "common_expenses", "cable_cost", "fire_cost"];
const parseCost = (value) => Number(String(value || 0).replace(/,/g, ""));

// สาเหตุการตัดหนี้
const CAUSE_OPTIONS = [
  { value: "1", label: "ลูกค้าชำระแล้ว" },
  { value: "2", label: "ลูกค้าเปลี่ยนสัญญา (บรรจุใหม่) ห้อง กคช." },
  { value: "3", label: "ลูกค้าบรรจุใหม่ ซื้อขายโอนกรรมสิทธิ์" },
];

// ช่องติ๊กของทุกรายการ ตั้งต้นเป็นไม่เลือก
const emptySelection = () => CUT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: false }), {});

export default class WriteOfDept extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null, // บิลค้างทุกงวดของบ้านหลังนี้
      date: new Date(),
      address_number: "",
      full_name: "",
      // ฟอร์มตัดหนี้
      ref_cause: "1",
      ref_remark: "",
      ref_iw_number: "",
      ref_record_date: "",
      total: 0, // ยอดที่เลือกตัด
      cut_mode: "old", // "old" = ตัดทั้งยอด | "new" = เลือกตัดเฉพาะรายการ
    };
  }

  async componentDidMount() {
    loading(false);
    const address_number = this.props.location?.query?.address_number;
    if (address_number) await this.GetById(address_number);
  }

  // ---------- โหลดข้อมูล ----------

  // ตัดช่องที่ถูกตัดหนี้ไปแล้ว (writeoff_list) ให้เป็น 0 แล้วคิดยอดใหม่
  applyWriteoff = (item) => {
    if (!item.writeoff_list?.length) return;

    BILL_FIELDS.forEach(({ k, sk }) => {
      if (item.writeoff_list.some((p) => p[sk])) item[k] = 0;
    });

    item.total_amount = BILL_FIELDS.reduce((sum, { k, m = 1 }) => sum + Number(item[k] || 0) * m, 0);
  };

  // ช่องนี้จบแล้วหรือยัง — ยึด transaction_payments/writeoff_list เท่านั้น ไม่อ่าน s_* ระดับงวด
  // (หลังยกเลิกการตัดหนี้/ยกเลิกใบเสร็จ backend ไม่ได้ล้าง s_* ยอดที่กลับมาค้างจะตัดหนี้ซ้ำไม่ได้)
  isSettled = (item, sk) => isWaived(item, sk);

  GetById = async (address_number) => {
    loading(true);
    const result = await GET(tokens, `v1/TH/record-receipt/filter-search/page/0/${getStorage("project_id")}`, { address_number });

    if (result?.status) {
      // ไม่มีบิลค้างเลย → กลับหน้ารายการ
      if (!result.data.data_water.length) {
        window.location.href = "/water-payment-info";
        return;
      }

      result.data.data_water.forEach(this.applyWriteoff);

      const resData = result.data.data_water
        .map((d) => {
          COMMA_FIELDS.forEach((k) => (d[k] = parseCost(d[k])));

          // ยอดค้างที่ยังตัดได้ = ช่องที่ยังไม่ถูกชำระ/ตัดหนี้
          let sum = 0;
          BILL_FIELDS.forEach(({ k, sk, m = 1 }) => {
            const settled = this.isSettled(d, sk);
            d[sk] = settled; // sync ให้ตาราง/ยอดรวมด้านล่างใช้สถานะเดียวกัน
            if (!settled) sum += (d[k] || 0) * m;
            else d[k] = 0;
          });

          // NOTE เดิมมีบรรทัด sum += d.late_payment ต่อจากลูปด้านบนอีกที
          //      ทำให้บิลที่ยังไม่ได้ชำระค่าปรับถูกนับค่าปรับ 2 เท่าในยอดตัดหนี้
          //      ลบออกแล้ว — ค่าปรับถูกนับใน BILL_FIELDS ของลูปด้านบนอยู่แล้ว

          return { ...d, total_amount: sum, sel: emptySelection() };
        })
        .sort((a, b) => new Date(a.datetime_create) - new Date(b.datetime_create));

      this.setState({ data: resData, address_number: resData[0].address_number, full_name: resData[0].full_name });
    }
    loading(false);
  };

  // ---------- บันทึกการตัดหนี้ ----------

  // ข้อมูลอ้างอิงที่ส่งไปพร้อมทั้ง 2 โหมด
  refPayload = () => ({
    ref_iw_number: this.state.ref_iw_number,
    ref_record_date: this.state.ref_record_date,
    ref_cause: Number(this.state.ref_cause),
    ref_remark: this.state.ref_remark,
  });

  // โหมดตัดทั้งยอด — ส่งเฉพาะ record_water_id ของงวดที่ติ๊กไว้
  Update = async () => {
    loading(true);
    const payload = {
      record_water_list: (this.state.data || []).filter((item) => item.selected).map((item) => item.record_water_id),
      ...this.refPayload(),
    };
    const result = await PUT(tokens, `v1/TH/record-water/clear-bill/${getStorage("project_id")}`, payload);
    if (result?.status) success_url("/water-payment-info");
    loading(false);
  };

  // โหมดเลือกตัดเฉพาะรายการ — ส่งว่าแต่ละงวดตัดช่องไหนบ้าง (คนละ endpoint กับโหมดเดิม)
  UpdateItem = async () => {
    const items = (this.state.data || [])
      .map((item) => {
        const sel = item.sel || {};
        const selected = {
          water_bill: !!sel.water_bill,
          equipment_maintenance_fee: !!sel.equipment_maintenance_fee,
          common_expenses: !!sel.common_expenses,
          waste_cost: !!sel.waste_cost,
          cable_cost: !!sel.cable_cost,
          other_income: !!sel.other_income,
          fire_cost: !!sel.fire_cost,
          // "เพิ่ม/ลดหนี้" เป็นช่องเดียวบนหน้าจอ แต่ backend เก็บแยก 2 ฟิลด์
          add_debt: !!sel.debt_adjust,
          reduce_debt: !!sel.debt_adjust,
          meter_connect_cost: !!sel.meter_connect_cost,
          late_payment: !!sel.late_payment,
          adjustment: !!sel.adjustment,
        };
        // งวดที่ไม่ได้ติ๊กอะไรเลย ไม่ต้องส่ง
        if (!Object.values(selected).some(Boolean)) return null;
        return {
          record_water_id: item.record_water_id,
          selected,
          adjustment_amount: sel.adjustment ? this.getFieldValue(item, CUT_FIELDS.find((f) => f.key === "adjustment")) : 0,
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      await alert_2("", "warning", "ยังไม่ได้เลือกรายการ", "กรุณาเลือกรายการที่ต้องการตัดหนี้ก่อน");
      return;
    }

    loading(true);
    const result = await PUT(tokens, `v1/TH/record-water/clear-bill-item/${getStorage("project_id")}`, { items, ...this.refPayload() });
    if (result?.status) success_url("/water-payment-info");
    loading(false);
  };

  handleSubmit = async () => {
    const confirmed = await alert_2("", "warning", "ยืนยันการตัดหนี้", `ต้องการตัดหนี้ยอด ฿${this.state.total ? toFixed(this.state.total) : "0.00"} ใช่หรือไม่?`, "ยืนยัน", "ยกเลิก");
    if (!confirmed) return;

    if (this.state.cut_mode === "new") await this.UpdateItem();
    else await this.Update();
  };

  // ---------- สลับโหมด ----------

  // สลับโหมดจะล้างรายการที่เลือกไว้ทั้งหมด (ถ้ามีการเลือกไว้แล้วจะถามยืนยันก่อน)
  switchMode = async (mode) => {
    if (mode === this.state.cut_mode) return;

    if (this.state.total > 0) {
      const confirmed = await alert_2("", "warning", "เปลี่ยนรูปแบบการตัดยอด", "การสลับรูปแบบจะล้างรายการที่เลือกไว้ทั้งหมด ต้องการดำเนินการต่อหรือไม่?", "เปลี่ยนรูปแบบ", "ยกเลิก");
      if (!confirmed) return;
    }

    const clearedData = (this.state.data || []).map((item) => ({ ...item, selected: false, sel: emptySelection() }));
    this.setState({ cut_mode: mode, data: clearedData, total: 0 });
  };

  // ---------- โหมดตัดทั้งยอด ----------

  cal_total = () => this.setState({ total: total((this.state.data || []).filter((item) => item.selected), "total_amount") });

  toggleAllRows = (checked) => this.setState({ data: this.state.data.map((d) => ({ ...d, selected: checked })) }, this.cal_total);

  toggleRow = (index, checked) => {
    const data = [...this.state.data];
    data[index].selected = checked;
    this.setState({ data }, this.cal_total);
  };

  // ---------- โหมดเลือกตัดเฉพาะรายการ ----------

  // ยอดของช่องหนึ่งในงวดหนึ่ง
  getFieldValue = (item, field) => (field.compute ? field.compute(item) : item[field.key] || 0);

  // ยอดรวมของทุกช่องที่ติ๊กไว้ ทุกงวด
  cal_total_new = () => {
    let sum = 0;
    (this.state.data || []).forEach((item) => {
      CUT_FIELDS.forEach((field) => {
        if (item.sel?.[field.key]) sum += this.getFieldValue(item, field);
      });
    });
    this.setState({ total: sum });
  };

  // สถานะ checkbox ของทั้งแถว — "none" | "partial" | "all"
  // นับเฉพาะช่องที่มียอด (ช่องที่เป็น 0 ติ๊กไม่ได้)
  getRowMasterState = (item) => {
    const selectable = CUT_FIELDS.filter((f) => this.getFieldValue(item, f) !== 0);
    if (!selectable.length) return "none";
    const selectedCount = selectable.filter((f) => item.sel?.[f.key]).length;
    if (selectedCount === 0) return "none";
    return selectedCount === selectable.length ? "all" : "partial";
  };

  // ติ๊กทั้งคอลัมน์ (เฉพาะแถวที่ช่องนั้นมียอด)
  toggleColumnAll = (fieldKey, checked) => {
    const field = CUT_FIELDS.find((f) => f.key === fieldKey);
    const data = (this.state.data || []).map((item) => (this.getFieldValue(item, field) === 0 ? item : { ...item, sel: { ...item.sel, [fieldKey]: checked } }));
    this.setState({ data }, this.cal_total_new);
  };

  // ติ๊กทั้งแถว (เฉพาะช่องที่มียอด)
  toggleRowMaster = (index, checked) => {
    const data = [...this.state.data];
    const item = data[index];
    const sel = { ...item.sel };
    CUT_FIELDS.forEach((field) => {
      if (this.getFieldValue(item, field) !== 0) sel[field.key] = checked;
    });
    data[index] = { ...item, sel };
    this.setState({ data }, this.cal_total_new);
  };

  toggleCell = (index, fieldKey, checked) => {
    const data = [...this.state.data];
    data[index] = { ...data[index], sel: { ...data[index].sel, [fieldKey]: checked } };
    this.setState({ data }, this.cal_total_new);
  };

  render() {
    const { data, date, address_number, full_name, ref_cause, ref_remark, total: totalAmount, ref_iw_number, ref_record_date, cut_mode } = this.state;

    const fmt = (value) => (value ? toFixed(value) : "0.00");
    const isNewMode = cut_mode === "new";
    const allSelected = data && data.length > 0 && data.every((item) => item.selected);

    // สรุปยอดที่เลือกของแต่ละงวด (แสดงใต้ยอดรวม เฉพาะโหมดเลือกตัดเฉพาะรายการ)
    const rowSelectedList = isNewMode
      ? (data || [])
          .map((item) => ({
            date: item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-",
            sum: CUT_FIELDS.reduce((s, f) => s + (item.sel?.[f.key] ? this.getFieldValue(item, f) : 0), 0),
          }))
          .filter((row) => row.sum !== 0)
      : [];

    const hasSelection = isNewMode ? rowSelectedList.length > 0 : !!data?.some((item) => item.selected);

    return (
      <div className="w-100 min-vh-100 fade-in">
        {/* NOTE เดิม page3 เขียนว่า "บันทึกรายรับอื่นๆ" (ก๊อปมาจากหน้าอื่น) ทั้งที่หน้านี้คือการตัดลูกหนี้ */}
        <Navbar page1="หน้าหลัก" page2="ข้อมูลจัดเก็บค่าใช้จ่าย" page3="การตัดลูกหนี้" />

        <div className="body">
          <div className="p-3 mx-3">
            {/* หัวข้อ + ปุ่มย้อนกลับ */}
            <div className="d-flex row mx-0 align-items-center justify-content-between">
              <h4 className="mb-3">
                <b>การตัดลูกหนี้</b>
              </h4>
              <button className="btn btn-outline-primary px-4 mx-1 mb-2" onClick={() => (window.location.href = "/water-payment-info")}>
                ย้อนกลับ
              </button>
            </div>

            {/* สลับโหมด */}
            <div className="d-flex align-items-center mb-3">
              <div className="btn-group">
                <button type="button" className={`mr-2 btn btn-sm ${cut_mode === "old" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => this.switchMode("old")}>
                  ตัดทั้งยอด (เดิม)
                </button>
                <button type="button" className={`btn btn-sm ${isNewMode ? "btn-primary" : "btn-outline-primary"}`} onClick={() => this.switchMode("new")}>
                  เลือกตัดเฉพาะรายการ (ใหม่)
                </button>
              </div>
            </div>

            {/* ข้อมูลห้อง (อ่านอย่างเดียว) */}
            <div className="card border-0 px-3 pt-3 mb-3">
              <div className="row">
                <div className="col-12 col-md-4 mb-3">
                  <label>วันที่</label>
                  <input type="text" className="form-control w-100 bg-contain border-0" value={format_date(date, "dd/mm/yyyy")} readOnly />
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label>บ้านเลขที่</label>
                  <input type="text" className="form-control w-100 bg-contain border-0" value={address_number} readOnly />
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label>ชื่อ - นามสกุล</label>
                  <input type="text" className="form-control w-100 bg-contain border-0" value={full_name} readOnly />
                </div>
              </div>
            </div>

            {/* ตารางบิลค้าง */}
            <div className="card border-0 p-3 mb-3">
              <div className="table-responsive">
                {!isNewMode ? (
                  /* ---------- โหมดตัดทั้งยอด ---------- */
                  <table key="old" className="table table-borderless table-striped text-nowrap">
                    <thead className="bg-dark-blue text-white">
                      <tr className="bg-primary">
                        <th className="text-center wpx-100">
                          <label className="d-flex align-items-center justify-content-center pointer mb-0" style={{ gap: 6 }}>
                            <input type="checkbox" className="pointer" checked={allSelected || false} onChange={(e) => this.toggleAllRows(e.target.checked)} />
                            <span>เลือก</span>
                          </label>
                        </th>
                        <th className="text-center wpx-100">วันที่จดมาตร</th>
                        <th className="text-center">เลขมาตรครั้งนี้</th>
                        <th className="text-center">เลขมาตรครั้งก่อน</th>
                        <th className="text-right">จำนวนหน่วยที่ใช้</th>
                        <th className="text-right">ค่าน้ำประปา</th>
                        <th className="text-right">ค่ารักษาอุปกรณ์น้ำ</th>
                        <th className="text-right">ค่าส่วนกลาง</th>
                        <th className="text-right">ค่าขยะ</th>
                        <th className="text-right">ค่าเคเบิล</th>
                        <th className="text-right">ค่าใช้จ่ายอื่นๆ</th>
                        <th className="text-right">ค่าเบี้ยประกัน</th>
                        <th className="text-right">เพิ่ม/ลดหนี้</th>
                        <th className="text-right">ค่าต่อกลับมิเตอร์</th>
                        <th className="text-right">ค่าปรับชำระล่าช้า</th>
                        <th className="text-right">ปรับปรุงยอดเงิน</th>
                        <th className="text-right">รวมยอดเงิน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data &&
                        data.map((item, index) => (
                          <tr key={index}>
                            <td className="text-center">
                              <input type="checkbox" className="pointer" checked={item.selected} onChange={(e) => this.toggleRow(index, e.target.checked)} />
                            </td>
                            <td className="text-center wpx-100">{item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-"}</td>
                            <td className="text-center">{item.current_gauge_number || "0"}</td>
                            <td className="text-center">{item.last_gauge_number || "0"}</td>
                            <td className="text-right">{item.unit_used || "-"}</td>
                            <td className="text-right">{fmt(item.water_bill)}</td>
                            <td className="text-right">{fmt(item.equipment_maintenance_fee)}</td>
                            <td className="text-right">{fmt(item.common_expenses)}</td>
                            <td className="text-right">{fmt(item.waste_cost)}</td>
                            <td className="text-right">{fmt(item.cable_cost)}</td>
                            <td className="text-right">{fmt(item.other_income)}</td>
                            <td className="text-right">{fmt(item.fire_cost)}</td>
                            <td className="text-right">{fmt((item.add_debt || 0) - (item.reduce_debt || 0))}</td>
                            <td className="text-right">{fmt(item.meter_connect_cost)}</td>
                            <td className="text-right">{fmt(item.late_payment)}</td>
                            {/* ปรับปรุงยอดเงินยังไม่รองรับในโหมดนี้ แสดง 0.00 คงที่ */}
                            <td className="text-right">0.00</td>
                            <td className="text-right">{fmt(item.total_amount)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  /* ---------- โหมดเลือกตัดเฉพาะรายการ ---------- */
                  <table key="new" className="table table-borderless table-striped text-nowrap">
                    <thead className="bg-dark-blue text-white">
                      <tr className="bg-primary">
                        <th className="text-center wpx-100">เลือกแถว</th>
                        <th className="text-center wpx-100">วันที่จดมาตร</th>
                        <th className="text-center">เลขมาตรครั้งนี้</th>
                        <th className="text-center">เลขมาตรครั้งก่อน</th>
                        <th className="text-right">จำนวนหน่วยที่ใช้</th>
                        {/* หัวคอลัมน์มี checkbox ติ๊กทั้งคอลัมน์ — คอลัมน์ที่ทุกแถวเป็น 0 จะติ๊กไม่ได้ */}
                        {CUT_FIELDS.map((field) => {
                          const values = (data || []).map((item) => this.getFieldValue(item, field));
                          const colAllZero = values.every((v) => v === 0);
                          const colChecked = !colAllZero && (data || []).every((item) => this.getFieldValue(item, field) === 0 || item.sel?.[field.key]);
                          return (
                            <th className="text-right" key={field.key}>
                              <label className="d-flex flex-row align-items-end pointer mb-0">
                                <span>{field.label}</span>
                                <input type="checkbox" className="ml-2 mt-1 pointer" disabled={colAllZero} checked={colChecked} onChange={(e) => this.toggleColumnAll(field.key, e.target.checked)} />
                              </label>
                            </th>
                          );
                        })}
                        <th className="text-right">รวมยอดที่เลือก</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data &&
                        data.map((item, index) => {
                          const rowState = this.getRowMasterState(item);
                          const rowSelectedTotal = CUT_FIELDS.reduce((s, field) => s + (item.sel?.[field.key] ? this.getFieldValue(item, field) : 0), 0);
                          return (
                            <tr key={index}>
                              {/* checkbox ทั้งแถว — ติ๊กบางช่องจะแสดงสถานะกึ่งกลาง (indeterminate) */}
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="pointer"
                                  ref={(el) => {
                                    if (el) el.indeterminate = rowState === "partial";
                                  }}
                                  checked={rowState === "all"}
                                  onChange={(e) => this.toggleRowMaster(index, e.target.checked)}
                                />
                              </td>
                              <td className="text-center wpx-100">{item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-"}</td>
                              <td className="text-center">{item.current_gauge_number || "0"}</td>
                              <td className="text-center">{item.last_gauge_number || "0"}</td>
                              <td className="text-right">{item.unit_used || "-"}</td>
                              {CUT_FIELDS.map((field) => {
                                const value = this.getFieldValue(item, field);
                                const checked = !!item.sel?.[field.key];
                                return (
                                  <td className="text-right" key={field.key}>
                                    <label className="d-flex align-items-center justify-content-end pointer mb-0">
                                      <span className={checked ? "text-primary font-weight-bold mr-2" : value === 0 ? "text-muted mr-2" : "mr-2"}>{fmt(value)}</span>
                                      <input type="checkbox" className="pointer" disabled={value === 0} checked={checked} onChange={(e) => this.toggleCell(index, field.key, e.target.checked)} />
                                    </label>
                                  </td>
                                );
                              })}
                              <td className="text-right">
                                <b>{fmt(rowSelectedTotal)}</b>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* ยอดตัดหนี้ */}
            <div className="card border-0 p-3 mb-3">
              <div className="w-100">
                <b className="mb-2">ยอดตัดหนี้</b>
                <h4>
                  <b>฿{fmt(totalAmount)}</b>
                </h4>
                {isNewMode && (
                  <div className="mt-2">
                    {rowSelectedList.length === 0 ? (
                      <div className="text-muted small">ยังไม่ได้เลือกรายการ</div>
                    ) : (
                      rowSelectedList.map((row, i) => (
                        <div key={i} className="d-flex justify-content-start border-top py-1">
                          <span className="small">{row.date}</span>
                          <span className="text-muted mx-2 d-flex align-items-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                              <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                          </span>
                          <span className="small">฿{fmt(row.sum)}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* สาเหตุ + หมายเหตุ */}
            <div className="card border-0 px-3 pt-3 mb-3">
              <div className="row">
                <div className="col-12 mb-3">
                  <label>สาเหตุ</label>
                  <select className="form-control bg-contain border-0" onChange={(e) => this.setState({ ref_cause: e.target.value })} value={ref_cause}>
                    {CAUSE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 mb-3">
                  <label>หมายเหตุเพิ่มเติม</label>
                  <input type="text" className="form-control w-100 bg-contain border-0" onChange={(e) => this.setState({ ref_remark: e.target.value })} value={ref_remark} />
                </div>
              </div>
            </div>

            {/* เอกสารอ้างอิง */}
            <div className="card border-0 px-3 pt-3 mb-3">
              <h4 className="mb-3">
                <b>เอกสารอ้างอิง</b>
              </h4>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label>เลขที่ใบเสร็จ</label>
                  <input type="text" className="form-control w-100 bg-contain border-0" onChange={(e) => this.setState({ ref_iw_number: e.target.value })} value={ref_iw_number} />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label>วันออกใบเสร็จ</label>
                  <DatePicker className="form-control bg-contain border-0 w-100" placeholder="กรอกข้อมูล" position="top" onChange={(e) => this.setState({ ref_record_date: e })} value={ref_record_date} />
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button className="btn btn-primary mb-4 wpx-200" disabled={!hasSelection} onClick={this.handleSubmit}>
                ตัดหนี้
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
